import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { Favorite, FavoriteTarget } from "../types/favorite";
import {
  addFavorite,
  getFavorites,
  removeFavorite,
} from "../utils/favoriteAPI";

export const favoritesQueryKey = (userId: string) =>
  ["favorites", userId] as const;

const findFavorite = (
  favorites: Favorite[] | undefined,
  modelType: FavoriteTarget,
  objectId: string | undefined
) =>
  favorites?.find(
    (favorite) =>
      favorite.model_type === modelType && favorite.object_id === objectId
  );

export const useFavorites = (userId: string | undefined) =>
  useQuery({
    queryKey: favoritesQueryKey(userId ?? "anonymous"),
    queryFn: () => getFavorites(),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });

type ToggleFavoriteResult = {
  action: "added" | "removed";
  favorite: Favorite | null;
};

export const useFavorite = (
  userId: string | undefined,
  modelType: FavoriteTarget,
  objectId: string | undefined
) => {
  const queryClient = useQueryClient();
  const favoritesQuery = useFavorites(userId);
  const favorite = findFavorite(favoritesQuery.data, modelType, objectId);

  const toggleMutation = useMutation({
    mutationFn: async (): Promise<ToggleFavoriteResult> => {
      if (!userId || !objectId) {
        throw new Error("A user and favorite target are required");
      }

      const queryKey = favoritesQueryKey(userId);
      const cachedFavorites = queryClient.getQueryData<Favorite[]>(queryKey);
      const currentFavorite =
        findFavorite(cachedFavorites, modelType, objectId) ?? favorite;

      try {
        if (currentFavorite) {
          await removeFavorite(currentFavorite.id);
          return { action: "removed", favorite: null };
        }

        const addedFavorite = await addFavorite(modelType, objectId);
        return { action: "added", favorite: addedFavorite };
      } catch (requestError) {
        // Synchronize after duplicate POSTs and uncertain network responses.
        const refreshedFavorites = await getFavorites();
        queryClient.setQueryData(queryKey, refreshedFavorites);

        const refreshedFavorite = findFavorite(
          refreshedFavorites,
          modelType,
          objectId
        );

        if (Boolean(refreshedFavorite) !== Boolean(currentFavorite)) {
          return refreshedFavorite
            ? { action: "added", favorite: refreshedFavorite }
            : { action: "removed", favorite: null };
        }

        throw requestError;
      }
    },
    onSuccess: (result) => {
      if (!userId || !objectId) return;

      queryClient.setQueryData<Favorite[]>(
        favoritesQueryKey(userId),
        (currentFavorites = []) => {
          const withoutTarget = currentFavorites.filter(
            (item) =>
              item.model_type !== modelType || item.object_id !== objectId
          );

          return result.favorite
            ? [...withoutTarget, result.favorite]
            : withoutTarget;
        }
      );
    },
  });

  return {
    favorite,
    isFavorite: Boolean(favorite),
    isLoading: favoritesQuery.isLoading || toggleMutation.isPending,
    error: favoritesQuery.error ?? toggleMutation.error,
    toggleFavorite: toggleMutation.mutateAsync,
  };
};

export const useRemoveFavorite = (userId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (favorite: Favorite): Promise<Favorite> => {
      if (!userId) {
        throw new Error("A user is required to remove a favorite");
      }

      try {
        await removeFavorite(favorite.id);
        return favorite;
      } catch (requestError) {
        // If the DELETE reached the server but its response was lost, confirm
        // the result from the list endpoint before reporting a failure.
        const refreshedFavorites = await getFavorites();
        queryClient.setQueryData(
          favoritesQueryKey(userId),
          refreshedFavorites
        );

        const stillExists = refreshedFavorites.some(
          (item) => item.id === favorite.id
        );

        if (!stillExists) {
          return favorite;
        }

        throw requestError;
      }
    },
    onSuccess: (removedFavorite) => {
      if (!userId) return;

      queryClient.setQueryData<Favorite[]>(
        favoritesQueryKey(userId),
        (currentFavorites = []) =>
          currentFavorites.filter((item) => item.id !== removedFavorite.id)
      );
    },
  });
};
